import axios from 'axios';
import { Student, Test, TestResult, LoginRequest, LoginResponse } from '../types';

// ponytail: Pages _redirects 200-rewrite only forwards GET, so POST/PUT/DELETE 405.
// Hit the Worker directly in prod (CORS already allows it); keep vite proxy for dev.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect to login if this is not a login request itself
      // Login failures should be handled by the Login component
      const isLoginRequest = error.config?.url?.includes('/auth/login');

      if (!isLoginRequest) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: (credentials: LoginRequest) =>
    api.post<LoginResponse>('/auth/login', credentials),

  logout: () =>
    api.post('/auth/logout'),

  getMe: () =>
    api.get('/auth/me'),

  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.put('/auth/change-password', data),

  createUser: (userData: { username: string; password: string; role: 'admin' | 'student'; student_id?: number }) =>
    api.post('/auth/users', userData),
};

export const studentsApi = {
  getAll: () =>
    api.get<Student[]>('/students'),

  getById: (id: number) =>
    api.get<Student>(`/students/${id}`),

  create: (student: Omit<Student, 'id' | 'created_at' | 'updated_at'>) =>
    api.post<Student>('/students', student),

  update: (id: number, student: Partial<Student>) =>
    api.put<Student>(`/students/${id}`, student),

  delete: (id: number) =>
    api.delete(`/students/${id}`),

  getTestResults: (id: number) =>
    api.get<TestResult[]>(`/students/${id}/test-results`),

  getClasses: () =>
    api.get<string[]>('/students/classes'),

  updateProfilePicture: (id: number, file: File) => {
    const formData = new FormData();
    formData.append('profile_picture', file);
    return api.put<Student>(`/students/${id}/profile-picture`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  deleteProfilePicture: (id: number) =>
    api.delete<Student>(`/students/${id}/profile-picture`),

  bulkCreate: (students: Array<{ name: string; phone_number: string; class?: string }>) =>
    api.post<{ results: Array<{ index: number; success: boolean; student?: Student; error?: string }> }>('/students/bulk', { students }),
};

export const testsApi = {
  getAll: () =>
    api.get<Test[]>('/tests'),

  getById: (id: number) =>
    api.get<Test>(`/tests/${id}`),

  create: (test: Omit<Test, 'id' | 'created_at' | 'updated_at'>) =>
    api.post<Test>('/tests', test),

  update: (id: number, test: Partial<Test>) =>
    api.put<Test>(`/tests/${id}`, test),

  delete: (id: number) =>
    api.delete(`/tests/${id}`),
};

export const testResultsApi = {
  getAll: (params?: { class?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.class) {
      queryParams.append('class', params.class);
    }
    const queryString = queryParams.toString();
    return api.get<TestResult[]>(`/test-results${queryString ? `?${queryString}` : ''}`);
  },

  create: (result: Omit<TestResult, 'id' | 'created_at' | 'updated_at'>) =>
    api.post<TestResult>('/test-results', result),

  update: (id: number, result: Partial<TestResult>) =>
    api.put<TestResult>(`/test-results/${id}`, result),

  delete: (id: number) =>
    api.delete(`/test-results/${id}`),

  import: (rows: Array<{ phone_number: string; scores: Array<{ testName: string; score: number }> }>) =>
    api.post<{ results: Array<{ phone_number: string; testName: string; success: boolean; error?: string }> }>('/test-results/import', { rows }),
};

export default api;
